"use client";

/**
 * Error boundary for motion islands.
 *
 * Motion components drive GSAP against DOM that React owns. SplitText in
 * particular rewrites an element's children, so an unlucky interleaving of
 * a font-load callback, a soft navigation and React's own commit can throw
 * (typically `NotFoundError: Failed to execute 'removeChild' on 'Node'`).
 *
 * Without a boundary that throw unmounts the whole tree and the visitor
 * gets a blank page: a decorative animation taking down an entire treatment
 * page. This boundary contains the blast radius to the one island. The
 * fallback is the same markup with no animation attached, so the content
 * stays readable and correctly styled; only the movement is lost.
 *
 * Boundaries catch throws from a child's render AND from its effects, which
 * is where the motion code actually runs, so wrapping the animated
 * component (not its parent) is the right granularity.
 */
import { Component, type ReactNode } from "react";

interface MotionSafeProps {
  /** Static markup to render if the animated child throws. */
  fallback: ReactNode;
  children: ReactNode;
}

interface MotionSafeState {
  failed: boolean;
}

export class MotionSafe extends Component<MotionSafeProps, MotionSafeState> {
  state: MotionSafeState = { failed: false };

  static getDerivedStateFromError(): MotionSafeState {
    return { failed: true };
  }

  componentDidCatch(error: unknown) {
    // Surfaced in development and in the browser console in production, so
    // a degraded island is diagnosable rather than silent. Never rethrown.
    if (process.env.NODE_ENV !== "production") {
      console.error("[MotionSafe] motion island degraded to static:", error);
    }
  }

  render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}
